/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { FluidErrorTypes } from "@fluidframework/core-interfaces/internal";
// Omit `dataCorruptionError` and `dataProcessingError` from the list of values inherited from FluidErrorTypes
const { dataCorruptionError, dataProcessingError, ...FluidErrorTypesExceptDataTypes } = FluidErrorTypes;
/**
 * Different error types the Driver may report out to the Host.
 * @legacy
 * @alpha
 */
export const DriverErrorTypes = {
    // Inherit base error types
    ...FluidErrorTypesExceptDataTypes,
    /**
     * Some non-categorized (below) networking error
     * Include errors like  fatal server error (usually 500).
     */
    genericNetworkError: "genericNetworkError",
    /**
     * Access denied - user does not have enough privileges to open a file, or continue to operate on a file
     */
    authorizationError: "authorizationError",
    /**
     * File not found, or file deleted during session
     */
    fileNotFoundOrAccessDeniedError: "fileNotFoundOrAccessDeniedError",
    /**
     * We can not reach server due to computer being offline.
     */
    offlineError: "offlineError",
    /*
     * Unsupported client protocol
     */
    unsupportedClientProtocolVersion: "unsupportedClientProtocolVersion",
    /**
     * User does not have write permissions to a file, but is changing content of a file.
     * That might be indication of some data store error - data stores should not generate ops in readonly mode.
     */
    writeError: "writeError",
    /**
     * A generic fetch failure that indicates we were not able to get a response from the server.
     * This may be due to the client being offline (though, if we are able to detect offline state it will be
     * logged as an offlineError instead).  Other possibilities could be DNS errors, malformed fetch request,
     * CSP violation, etc.
     */
    fetchFailure: "fetchFailure",
    /**
     * This error occurs when token provider fails to fetch orderer token
     */
    fetchTokenError: "fetchTokenError",
    /**
     * Unexpected response from server. Either JSON is malformed, or some required properties are missing
     */
    incorrectServerResponse: "incorrectServerResponse",
    /**
     * This error occurs when the file is modified externally (not through Fluid protocol) in storage.
     * It will occur in cases where client has some state or cache that is based on old content (identity) of a file,
     * and storage / driver / loader detects such mismatch.
     * When it's hit, client needs to forget all the knowledge about this file and start over.
     */
    fileOverwrittenInStorage: "fileOverwrittenInStorage",
    /**
     * The document is read-only and delta stream connection is forbidden.
     */
    deltaStreamConnectionForbidden: "deltaStreamConnectionForbidden",
    /**
     * The location of file/container can change on server. So if the file location moves and we try to access the old
     * location, then this error is thrown to let the client know about the new location info.
     */
    locationRedirection: "locationRedirection",
    /**
     * When a file is not a Fluid file, but has Fluid extension such as ".note",
     * server won't be able to open it and will return this error. The innerMostErrorCode will be
     * "fluidInvalidSchema"
     */
    fluidInvalidSchema: "fluidInvalidSchema",
    /**
     * File is locked for read/write by storage, e.g. whole collection is locked and access denied.
     */
    fileIsLocked: "fileIsLocked",
    /**
     * Storage is out of space
     */
    outOfStorageError: "outOfStorageError",
};
//# sourceMappingURL=driverError.js.map